import {
  Job,
  JobStopManifestSequence,
  Manifest,
  ManifestStop,
  Maybe,
  NestedJobStop,
  Stop
} from "../../generated/graphql";

import {TopStopInformationValue} from "../../components/manifest/card-configuration/v2/constants/enums";
import {clone} from "lodash";
import {getStopCompletedTime} from "./StopTimeUtils";
import {TManifestStopEnhanced} from "../../components/common/types";

export enum JobStopStatus {
  ACTIVE = "A",
  COMPLETED = "C",
  QUEUED = "Q"
}

export enum JobStopType {
  PICKUP = "P",
  DELIVERY = "D",
  BOTH = "B"
}

export const jobStopTypeToString = ({value}: {value: string}) => {
  switch (value) {
    case JobStopType.PICKUP:
      return "Pickup";
    case JobStopType.DELIVERY:
      return "Delivery";
    case JobStopType.BOTH:
      return "Both";
    default:
      return "";
  }
};

export const jobStopStatusToString = (jobStopStatus: any) => {
  switch (jobStopStatus) {
    case JobStopStatus.COMPLETED:
      return "Completed";
    case JobStopStatus.ACTIVE:
      return "Active";
    case JobStopStatus.QUEUED:
      return "Queued";
    default:
      return "";
  }
};

export const isPickupStop = (stop: NestedJobStop | ManifestStop) => stop.stopType === JobStopType.PICKUP;
export const isDeliveryStop = (stop: NestedJobStop | ManifestStop) => stop.stopType === JobStopType.DELIVERY;
export const isBothStop = (stop: NestedJobStop | ManifestStop) => stop.stopType === JobStopType.BOTH;

export const isPickupOrBothStop = (stop: ManifestStop) =>
  stop.stopType === JobStopType.PICKUP || stop.stopType === JobStopType.BOTH;

export const isDeliveryOrBothStop = (stop: ManifestStop) =>
  stop.stopType === JobStopType.DELIVERY || stop.stopType === JobStopType.BOTH;

export const pickupStop = (stops: NestedJobStop[]): NestedJobStop | undefined => {
  if (stops && stops.length > 0) {
    return stops.find((stop) => stop.stopType === "P" || stop.stopType === "B");
  }
  return undefined;
};

export const deliveryStop = (stops: NestedJobStop[]): NestedJobStop | undefined => {
  if (stops && stops.length > 0) {
    const reversedStops = stops.slice().reverse();
    return reversedStops.find((stop) => stop.stopType === "D" || stop.stopType === "B");
  }
  return undefined;
};

export const activeStops = (stops: readonly ManifestStop[] | Maybe<ManifestStop[]> | undefined): number =>
  stops?.filter(isActiveOrQueued).length ?? 0;

export const completedStops = (stops: readonly ManifestStop[] | Maybe<ManifestStop[]> | undefined): number =>
  stops?.filter(isCompleted).length ?? 0;

export const isActive = (stop: ManifestStop | NestedJobStop): boolean => stop.jobStopStatus === JobStopStatus.ACTIVE;

export const isActiveOrQueued = (stop: ManifestStop): boolean =>
  stop.jobStopStatus === JobStopStatus.ACTIVE || stop.jobStopStatus === JobStopStatus.QUEUED;

export const hasQueuedStops = (stops: readonly ManifestStop[] | Maybe<ManifestStop[]> | undefined): boolean =>
  stops?.some(isQueued) ?? false;

export const isCompleted = (stop: ManifestStop | NestedJobStop | Stop): boolean =>
  stop.jobStopStatus === JobStopStatus.COMPLETED;

export const isInCompleted = (stop: ManifestStop | NestedJobStop | Stop): boolean =>
  stop.jobStopStatus !== JobStopStatus.COMPLETED;

export const isPartiallyCompletedJob = (job: Job): boolean =>
  job.stops.some(isCompleted) && !job.stops.every(isCompleted);

export const isOnSite = (stop: ManifestStop): boolean => {
  if (stop.arriveDateTime && !stop.departDateTime && !isCompleted(stop)) {
    return true;
  }
  return false;
};
export const isQueued = (stop: ManifestStop): boolean => stop.jobStopStatus === JobStopStatus.QUEUED;

export const getNextActiveStop = (manifest: Manifest): ManifestStop | undefined => {
  return getNextActiveStops(manifest, 1)?.[0] || undefined;
};
export const getNextActiveStops = (manifest: Manifest, stops: number): ManifestStop[] | undefined => {
  return manifest.stops?.filter(isActiveOrQueued).sort(sequenceComparator).slice(0, stops);
};

export const getAllActiveStops = (manifest: Manifest): ManifestStop[] | undefined => {
  return manifest.stops?.filter(isActiveOrQueued).sort(sequenceComparator);
};

export const getFinalCompletedStop = (manifest: Manifest): ManifestStop | undefined => {
  if (manifest.stops?.every(isCompleted)) {
    return [...manifest.stops].sort(sequenceComparator)[manifest.stops?.length - 1];
  }
  return undefined;
};

export const isStopsAreSameJob = (stops: ManifestStop[]): boolean => {
  return stops.every((stop) => stop.job.jobId === stops[0].job.jobId);
};

export const isJobAlreadySplit = (stops: ManifestStop[] | undefined): boolean => {
  return stops?.some((stop) => !!stop.job.driverId2) ?? false;
};

export const getTopStopByConfig = (
  manifest: Manifest,
  topStopInfo: TopStopInformationValue
): ManifestStop | undefined => {
  switch (topStopInfo) {
    case TopStopInformationValue.LastCompletedStop: {
      return getLastCompletedStop(manifest);
    }

    case TopStopInformationValue.FirstStopInManifest: {
      return getFirstStop(manifest);
    }

    case TopStopInformationValue.NextStopInManifest: {
      return getNextActiveStops(manifest, 1)?.at(0);
    }

    default:
  }
};

export const getOnSiteStop = (manifest: Manifest): ManifestStop | undefined => {
  return manifest.stops?.find(isOnSite);
};

export const getLastCompletedStop = (manifest: Manifest): ManifestStop | undefined => {
  return manifest.stops?.filter(isCompleted).sort(completedTimeComparator).at(-1);
};

export const getFirstStop = (manifest: Manifest): ManifestStop | undefined => {
  return clone(manifest.stops)?.sort(sequenceComparator).at(0);
};

export const addOptimizedSequenceToStops = (sequences: JobStopManifestSequence[], manifest: Manifest) => {
  if (sequences.length === 0) return [];
  const manifestStops = manifest.stops;
  const newManifestStops: TManifestStopEnhanced[] = [];
  sequences.forEach((sequence) => {
    const stop = manifestStops?.find((stop) => stop.jobStopId === sequence.jobStopId);
    if (stop) {
      newManifestStops.push({...stop, optimizedSequence: sequence.manifestSequence});
    }
  });
  const remainingStops =
    manifestStops?.filter((s) => !newManifestStops.some((ns) => ns.jobStopId === s.jobStopId)) ?? [];

  return newManifestStops.concat(remainingStops);
};

export const sequenceComparator = (
  a: NestedJobStop | ManifestStop | undefined,
  b: NestedJobStop | ManifestStop | undefined
): number => {
  if (a?.manifestSequence && b?.manifestSequence) {
    return (a.manifestSequence || 0) - (b.manifestSequence || 0);
  } else {
    return (a?.sequence ?? 0) - (b?.sequence ?? 0);
  }
};

export const completedTimeComparator = (a: NestedJobStop | ManifestStop, b: NestedJobStop | ManifestStop): number => {
  const aTime = new Date(getStopCompletedTime(a) ?? 0).getTime();
  const bTime = new Date(getStopCompletedTime(b) ?? 0).getTime();
  if (aTime === bTime) {
    return sequenceComparator(a, b);
  }
  return aTime - bTime;
};

/**
 * Sorts stops by scheduledDateTime (ascending), then by sequence (ascending) if available.
 * @param stops Array of stops to sort
 * @param direction Sort direction, either 'asc' or 'desc' (default is 'asc')
 * @returns New sorted array
 */
export const sortStopsByScheduledAndSequence = (
  stops: NestedJobStop[],
  direction: "asc" | "desc" = "asc"
): NestedJobStop[] => {
  const sorted = [...stops].sort((a, b) => {
    const aTime = a.scheduledDateTime ? new Date(a.scheduledDateTime).getTime() : 0;
    const bTime = b.scheduledDateTime ? new Date(b.scheduledDateTime).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return (a.sequence ?? 0) - (b.sequence ?? 0);
  });
  return direction === "desc" ? sorted.reverse() : sorted;
};
